import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import {
  eventModeLeftVersion, inEventMode, readEventModeLeft, rememberEventModeLeft, rostersNeeded, subscribeEventModeLeft,
  type EventModeInput,
} from './eventMode';
import { eventIntelligenceAccessQuery } from './useEventIntelligenceAccess';
import { eventsListQuery } from './eventsListQuery';
import type { EventSummary, EventWorkspace } from './types';

const MINUTE = 60_000;

// A minute-resolution clock, so event mode switches itself off once the event's end passes.
function subscribeMinute(onChange: () => void) {
  const timer = window.setInterval(onChange, 15_000);
  return () => window.clearInterval(timer);
}
const minuteNow = () => Math.floor(Date.now() / MINUTE) * MINUTE;

export interface EventModeData {
  // Still asking the server whether this rep has a live event.
  pending: boolean;
  input: EventModeInput | null;
}

// Everything event mode decides on: the flag, the rep's events, admins' rosters for live events
// and what they left today. Fails closed: no flag, an error or no user means no event mode.
export function useEventModeData(enabled = true): EventModeData {
  const { user } = useAuth();
  const access = useQuery({ ...eventIntelligenceAccessQuery(user?.id), enabled: enabled && !!user?.id });
  const allowed = enabled && access.data?.enabled === true;
  const events = useQuery({ ...eventsListQuery(user?.id), enabled: allowed && !!user });
  const now = useSyncExternalStore(subscribeMinute, minuteNow);
  const viewer = useMemo(() => (user ? { id: user.id, admin: user.role === 'Admin' } : null), [user]);
  const needed = allowed ? rostersNeeded(events.data, viewer, now) : [];
  const rosterQueries = useQueries({
    queries: needed.map((id) => ({
      // Kept out of the event resource keys: those refetch after every capture.
      queryKey: ['eventModeRoster', user?.id, id],
      queryFn: async () => (await api.get<EventWorkspace>(`/events/${id}`, { headers: { 'X-Meridian-Event-Actor': user?.id } })).data.representativeIds,
      staleTime: 5 * MINUTE,
      retry: false,
    })),
  });
  // Leaving re-renders every reader, so the event menu drops its event-mode rows at once.
  useSyncExternalStore(subscribeEventModeLeft, eventModeLeftVersion);

  const pending = !!user && enabled && (access.isPending || (allowed && events.isPending) || rosterQueries.some((query) => query.isPending));
  let input: EventModeInput | null = null;
  if (viewer && allowed && events.data) {
    const rosters = Object.fromEntries(needed.map((id, index) => [id, rosterQueries[index]?.data ?? []]));
    const leftDays = Object.fromEntries(events.data.map((e) => [e.id, readEventModeLeft(viewer.id, e.id)]));
    input = { events: events.data, viewer, rosters, leftDays, now };
  }
  return { pending, input };
}

export interface ActiveEventMode {
  event: EventSummary | null;
  leave: () => void;
}

// The event mode for the event on screen: set while that event is live, the rep works it and
// hasn't left it today on this device.
export function useActiveEventMode(eventId: string | null, enabled = true): ActiveEventMode {
  const { user } = useAuth();
  const { input } = useEventModeData(enabled && !!eventId);
  const event = eventId && input ? inEventMode(eventId, input) : null;
  const leave = useCallback(() => { if (user && event) rememberEventModeLeft(user.id, event); }, [user, event]);
  return { event, leave };
}
